import React, { useMemo } from 'react';

// import styled from '@emotion/styled';
import { Box, Button, Container, Divider, Link, Stack, Theme, Typography, useTheme } from '@mui/material';


import { LinkGroupProps, LinkList, StrapiContentStatus } from '@ajc/lib-mui';
import { Circle, Email, Facebook, Instagram, LinkedIn, Phone, YouTube } from '@mui/icons-material';
import { Link as GatsbyLink, graphql, useStaticQuery } from 'gatsby';

const requiredPublishType: StrapiContentStatus = process.env.GATSBY_STRAPI_CONTENT_STATUS

const useStyles = (theme: Theme) => ({
    background: {
        background: '#445239',
        color: 'white',
        pt: { xs: 4, sm: 4, md: 6 },
        pb: { xs: 4, sm: 5, md: 6 },
        borderTop: '3px solid #11140e'
    },
})

function SocialLink({ platform, url }: { platform: 'Facebook' | 'LinkedIn' | 'YouTube' | 'Instagram', url: string }) {
    let icon: any;
    switch (platform) {
        case 'Facebook':
            icon = <Facebook fontSize='small' />;
            break;
        case 'LinkedIn':
            icon = <LinkedIn fontSize='small' />;
            break;
        case 'YouTube':
            icon = <YouTube fontSize='small' />;
            break;
        case 'Instagram':
            icon = <Instagram fontSize='small' />;
            break;
        default:
            icon = <Circle fontSize='small' />;
    }

    return (
        <Stack direction={"row"} gap={1} sx={{ color: 'inherit' }}>
            {icon}
            <Link href={url} target="_blank" rel="noopener noreferrer" variant='body2' sx={{ color: 'inherit', textDecoration: 'underline' }}>{platform}</Link>
        </Stack>
    )
}

export default function Footer() {
    const theme = useTheme();
    const styles = useStyles(theme)

    const footerDataQuery: Queries.FooterComponentQuery = useStaticQuery(graphql`
    query FooterComponent {
      strapiWebsiteInfo {
        # contactEmail: ContactEmail
        # contactPhoneText: ContactPhoneText
        # contactPhoneE164: ContactPhoneE164
        companyName: CompanyName
        # contactPhoneLong: ContactPhoneLong
        # companyAddress: CompanyAddress
        postalAddress: PostalAddress
        # socials: Socials {
        #   platform: Platform
        #   url: URL
        # }
      }
      strapiFooter {
        logo: Logo {          
          localFile {
            childImageSharp {
              gatsbyImageData(placeholder: NONE, width: 230, quality: 100)
            }
          }
        }
        disclaimerTitle: DisclaimerTitle
        disclaimerContent: DisclaimerContent
        links: Links {
          url: URL
          label: Label
          hidden: Hidden
          links: Links {
            label: Label
            url: URL
            hidden: Hidden
          }
        }
      }
    }
  `)

    // const NotPrintable = useMemo(() => styled('div')(notPrintable), []);

    const footerProps = useMemo(() => {
        return { ...footerDataQuery.strapiFooter }
    }, [])

    const websiteInfoProps = useMemo(() => {
        return { ...footerDataQuery.strapiWebsiteInfo }
    }, [])

    const logo = footerProps?.logo?.localFile?.childImageSharp?.gatsbyImageData;

    const divider = <Divider sx={{ borderColor: '#445239', borderWidth: '1px' }} />

    const thisYear = useMemo(() => new Date().getFullYear(), [])

    const copyrightContent = () => {
        return <Stack gap={0.5}>
            <Typography variant="subtitle2" sx={{ fontSize: '1rem!important' }} color='inherit'>&copy; {thisYear} {websiteInfoProps.companyName}.</Typography>
            <Typography variant="body2" color='inherit'>All rights reserved.</Typography>
        </Stack>
    }

    const disclaimerContent = () => {
        return <Stack gap={2} sx={{ width: { xs: '100%', sm: '190px', md: '220px', lg: '200px' }, textAlign: { xs: 'center', sm: 'center', md: 'initial' } }}>
            <Typography variant="h6" color='inherit'>{footerProps.disclaimerTitle || ''}</Typography>
            <Typography variant="body2" color='inherit'>{footerProps.disclaimerContent}</Typography>
            <Box sx={{ display: { xs: 'none', sm: 'none', md: 'initial' } }}>
                {copyrightContent()}
            </Box>
        </Stack>
    }

    return (
        // <NotPrintable>
        <Box component="footer" sx={styles.background}>
            <Container color={'inherit'}>
                <Stack gap={2} color={'inherit'}>
                    {websiteInfoProps.companyName && (
                        <GatsbyLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Box sx={{ display: "none" }}>Home</Box>
                            {/* {logo && <GatsbyImage image={logo} alt="logo" loading='eager' />} */}
                            <Typography variant="h6" color='inherit' sx={{ fontWeight: '600', color: 'inherit' }} >{websiteInfoProps.companyName}</Typography>
                        </GatsbyLink>
                    )}

                    <Stack direction={{ xs: "column", sm: 'row', md: "row" }} spacing={{ xs: 2, sm: 2, md: 2, lg: 14 }} sx={{ width: '100%' }} justifyContent={{ xs: 'center', sm: 'space-between', md: 'space-between' }}>
                        <Box>
                            <Stack gap={1} justifyContent={{ xs: 'center', sm: 'space-around', md: 'space-around' }} sx={{ maxWidth: { xs: '100%', sm: '190px', md: '250px', lg: '250px' } }}>
                                <Typography variant="h6" color='inherit'>Contact Us</Typography>
                                <Stack gap={{ xs: 2, sm: 3, md: 4 }}>
                                    <Typography variant="body2" color='inherit'>Get in touch for enquiries, feedback, complaints and compliments.</Typography>

                                    <Stack gap={1}>
                                        {(websiteInfoProps.contactPhoneE164 || websiteInfoProps.contactPhoneText) && (
                                            <Stack direction='row' gap={1}>
                                                <Phone fontSize='small' color='inherit' />
                                                <Typography variant="body2" color='inherit' component="div">Phone: <Link sx={{ color: 'inherit' }} underline='none' href={websiteInfoProps.contactPhoneE164 ? 'tel:' + websiteInfoProps.contactPhoneE164 : undefined}>{websiteInfoProps.contactPhoneText || websiteInfoProps.contactPhoneE164}</Link></Typography>
                                            </Stack>
                                        )}

                                        {websiteInfoProps.contactEmail && (
                                            <Stack direction='row' gap={1}>
                                                <Email fontSize='small' color='inherit' />
                                                <Typography variant="body2" color='inherit' component="div">Email: <Link sx={{ color: 'inherit' }} underline='none' href={'mailto:' + websiteInfoProps.contactEmail}>{websiteInfoProps.contactEmail}</Link></Typography>
                                            </Stack>
                                        )}
                                    </Stack>

                                    <Box>
                                        <Button LinkComponent={GatsbyLink} variant='contained' color='secondary' size="small" href="/contact" {...{ to: '/contact' }}>Contact Us</Button>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Box>

                        <Stack divider={divider} direction={{ xs: "column", sm: 'row', md: "row" }} gap={{ xs: 3, sm: 2, md: 4 }} justifyContent={{ xs: 'center', sm: 'space-around', md: 'space-around' }}>
                            {footerProps?.links && (
                                <LinkList
                                    headerComponent="h6"
                                    forceLink
                                    requiredPublishType={requiredPublishType}
                                    links={footerProps?.links as LinkGroupProps[]}
                                    StackProps={{ direction: { xs: "row", sm: 'row', md: "row" }, justifyContent: { xs: 'space-between', sm: 'flex-start', md: 'center' }, gridAutoColumns: { xs: 2, sm: 2, md: 2 }, divider: divider, spacing: { xs: 3, sm: 3, md: 4 } }}
                                    relativeLinkComponent={GatsbyLink}
                                    LinkProps={{ underline: 'always' }}
                                    LinkGroupItemsTypographyProps={{ variant: 'body2', color: 'inherit' }}
                                />
                            )}

                            {/* {websiteInfoProps.socials && websiteInfoProps.socials.length > 0 && (
                                <Stack gap={2}>
                                    <Typography variant="h6" color='inherit'>Follow us</Typography>
                                    {
                                        websiteInfoProps.socials.map((social, i) => {
                                            if (!social || !social.platform || !social.url) return <></>;

                                            return <SocialLink key={i} platform={social.platform as any} url={social.url} />
                                        })
                                    }
                                </Stack>
                            )} */}

                            {/* <Box sx={{ display: { xs: 'none', sm: 'none', md: 'initial' } }}> */}
                            {disclaimerContent()}
                            {/* </Box> */}

                        </Stack>

                    </Stack>

                    {/* <Box sx={{ display: { xs: 'initial', sm: 'initial', md: 'none' } }}>
                        {disclaimerContent()}
                    </Box> */}
                    <Box sx={{ display: { xs: 'initial', sm: 'initial', md: 'none' }, textAlign: 'center' }}>
                        {copyrightContent()}
                    </Box>
                </Stack>
            </Container>
        </Box>
        // </NotPrintable>
    )
}
